import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Autocomplete,
  Slider
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Email, 
  Lock, 
  Public,
  TrackChanges,
  Restaurant,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface BasicFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface ConfigFormData {
  country: string;
  weeklyGoal: number;
  excludedVegetables: string[];
}

interface ConsentFormData {
  iaConsent: boolean;
}

interface ConfirmFormData {
  acceptedTerms: boolean;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  country?: string;
  weeklyGoal?: string;
}

// Lista de países/regiones
const countries = [
  'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica',
  'Cuba', 'Ecuador', 'El Salvador', 'España', 'Guatemala', 'Honduras',
  'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú', 'Puerto Rico',
  'República Dominicana', 'Uruguay', 'Venezuela'
];

// Lista de verduras comunes para excluir
const commonVegetables = [
  'Apio', 'Berenjena', 'Brócoli', 'Calabacín', 'Calabaza', 'Cebolla',
  'Col', 'Coliflor', 'Espárragos', 'Espinaca', 'Judías verdes', 'Lechuga',
  'Pepino', 'Pimiento', 'Remolacha', 'Repollo', 'Tomate', 'Zanahoria',
  'Acelgas', 'Alcachofa', 'Apio nabo', 'Berro', 'Bok choy', 'Coles de Bruselas',
  'Endivias', 'Hinojo', 'Kale', 'Nabo', 'Puerro', 'Rábano', 'Rúcula'
];

export default function UserRegister() {
  const [currentStep, setCurrentStep] = useState(1);
  const [basicFormData, setBasicFormData] = useState<BasicFormData>({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [configFormData, setConfigFormData] = useState<ConfigFormData>({
    country: '',
    weeklyGoal: 30,
    excludedVegetables: []
  });
  const [consentFormData, setConsentFormData] = useState<ConsentFormData>({
    iaConsent: false
  });
  const [confirmFormData, setConfirmFormData] = useState<ConfirmFormData>({
    acceptedTerms: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Validaciones para la primera pantalla
  const validateBasicForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!basicFormData.fullName.trim()) {
      newErrors.fullName = 'El nombre completo es obligatorio';
    } else if (basicFormData.fullName.trim().length < 2) {
      newErrors.fullName = 'El nombre debe tener al menos 2 caracteres';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicFormData.email.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio';
    } else if (!emailRegex.test(basicFormData.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido';
    }

    if (!basicFormData.password) {
      newErrors.password = 'La contraseña es obligatoria';
    } else if (basicFormData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!basicFormData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (basicFormData.password !== basicFormData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validaciones para la segunda pantalla
  const validateConfigForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!configFormData.country) {
      newErrors.country = 'Selecciona tu país o región';
    }

    if (configFormData.weeklyGoal < 20 || configFormData.weeklyGoal > 50) {
      newErrors.weeklyGoal = 'El objetivo debe estar entre 20 y 50 verduras';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validación para la pantalla de consentimiento
  const validateConsentForm = (): boolean => {
    // No es obligatorio, pero podrías requerir que el usuario acepte o lea la explicación
    return true;
  };

  // Validación para la pantalla de confirmación
  const validateConfirmForm = (): boolean => {
    if (!confirmFormData.acceptedTerms) {
      setSubmitError('Debes aceptar los Términos y Condiciones y la Política de Privacidad');
      return false;
    }
    setSubmitError(null);
    return true;
  };

  const handleBasicInputChange = (field: keyof BasicFormData, value: string) => {
    setBasicFormData((prev: BasicFormData) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: FormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleConfigInputChange = (field: keyof ConfigFormData, value: any) => {
    setConfigFormData((prev: ConfigFormData) => ({ ...prev, [field]: value }));
    if (field in errors) {
      setErrors((prev: FormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleConsentInputChange = (value: boolean) => {
    setConsentFormData({ iaConsent: value });
  };

  const handleConfirmInputChange = (value: boolean) => {
    setConfirmFormData({ acceptedTerms: value });
    setSubmitError(null);
  };

  const handleNextStep = async () => {
    if (currentStep === 1 && validateBasicForm()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateConfigForm()) {
      setCurrentStep(3);
    } else if (currentStep === 3 && validateConsentForm()) {
      setCurrentStep(4);
    } else if (currentStep === 4 && validateConfirmForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);
      try {
        const res = await fetch('/api/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: basicFormData.fullName,
            email: basicFormData.email,
            password: basicFormData.password,
            country: configFormData.country,
            weeklyGoal: configFormData.weeklyGoal,
            excludedVegetables: configFormData.excludedVegetables,
            iaConsent: consentFormData.iaConsent
          })
        });
        const data = await res.json();
        if (res.ok && data.ok) {
          setSubmitSuccess(true);
          setTimeout(() => {
            navigate('/login');
          }, 1500); // Espera 1.5 segundos para mostrar el mensaje de éxito
        } else {
          setSubmitError(data.error || 'Error desconocido al registrar');
        }
      } catch (err) {
        setSubmitError('Error de red o del servidor');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepProgress = () => {
    return (currentStep / 4) * 100;
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Paso 1 de 4: Datos básicos';
      case 2: return 'Paso 2 de 4: Configuración inicial';
      case 3: return 'Paso 3 de 4: Consentimiento IA';
      case 4: return 'Paso 4 de 4: Confirmación';
      default: return 'Registro';
    }
  };

  const renderBasicForm = () => (
    <>
      <Typography variant="h6" fontWeight={600} mb={3} color="primary">
        Información básica
      </Typography>

      <TextField
        fullWidth
        label="Nombre completo"
        value={basicFormData.fullName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBasicInputChange('fullName', e.target.value)}
        error={!!errors.fullName}
        helperText={errors.fullName}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person color="primary" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        type="email"
        label="Correo electrónico"
        value={basicFormData.email}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBasicInputChange('email', e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="primary" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        type={showPassword ? 'text' : 'password'}
        label="Contraseña"
        value={basicFormData.password}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBasicInputChange('password', e.target.value)}
        error={!!errors.password}
        helperText={errors.password || 'Mínimo 6 caracteres'}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        type={showConfirmPassword ? 'text' : 'password'}
        label="Confirmar contraseña"
        value={basicFormData.confirmPassword}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBasicInputChange('confirmPassword', e.target.value)}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          💡 Puedes cambiar esta información más tarde en tu perfil
        </Typography>
      </Alert>
    </>
  );

  const renderConfigForm = () => (
    <>
      <Typography variant="h6" fontWeight={600} mb={3} color="primary">
        Configuración inicial
      </Typography>

      {/* País o región */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>País o región</InputLabel>
        <Select
          value={configFormData.country}
          label="País o región"
          onChange={(e) => handleConfigInputChange('country', e.target.value)}
          error={!!errors.country}
          startAdornment={
            <InputAdornment position="start">
              <Public color="primary" />
            </InputAdornment>
          }
        >
          {countries.map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Select>
        {errors.country && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
            {errors.country}
          </Typography>
        )}
      </FormControl>

      {/* Objetivo semanal */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <TrackChanges sx={{ mr: 1 }} />
          Objetivo semanal de vegetales: {configFormData.weeklyGoal}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ¿Cuántos vegetales distintos quieres consumir cada semana?
        </Typography>
        <Slider
          value={configFormData.weeklyGoal}
          onChange={(_, value) => handleConfigInputChange('weeklyGoal', value)}
          min={20}
          max={50}
          step={5}
          marks={[
            { value: 20, label: '20' },
            { value: 30, label: '30' },
            { value: 40, label: '40' },
            { value: 50, label: '50' }
          ]}
          sx={{
            color: '#4caf50',
            '& .MuiSlider-thumb': {
              backgroundColor: '#4caf50'
            },
            '& .MuiSlider-track': {
              backgroundColor: '#4caf50'
            }
          }}
        />
        {errors.weeklyGoal && (
          <Typography variant="caption" color="error">
            {errors.weeklyGoal}
          </Typography>
        )}
      </Box>

      {/* Verduras a excluir */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <Restaurant sx={{ mr: 1 }} />
          Preferencias alimenticias
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Agrega los vegetales que no te gustan o no puedes conseguir (opcional)
        </Typography>
        <Autocomplete
          multiple
          options={commonVegetables}
          value={configFormData.excludedVegetables}
          onChange={(_, newValue) => handleConfigInputChange('excludedVegetables', newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                sx={{ bgcolor: '#ffebee', borderColor: '#e57373' }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar verduras a excluir"
              placeholder="Escribe para buscar..."
            />
          )}
        />
      </Box>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          🌱 Importante para filtrar vegentales locales y personalizar tu experiencia
        </Typography>
      </Alert>
    </>
  );

  const renderConsentForm = () => (
    <>
      <Typography variant="h6" fontWeight={600} mb={3} color="primary">
        Consentimiento para análisis con IA
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Si lo deseas, puedes activar el análisis automático de tus platos mediante IA. Esto te permitirá subir o tomar fotos de tus comidas y la app detectará automáticamente los vegetales presentes.<br /><br />
          <b>Privacidad:</b> Las imágenes se procesan de forma segura y solo se usan para mejorar tu experiencia nutricional.
        </Typography>
      </Alert>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" sx={{ mr: 2 }}>
          ¿Quieres usar el análisis con IA?
        </Typography>
        <Button
          variant={consentFormData.iaConsent ? 'contained' : 'outlined'}
          color={consentFormData.iaConsent ? 'success' : 'primary'}
          onClick={() => handleConsentInputChange(!consentFormData.iaConsent)}
          sx={{ borderRadius: 3, fontWeight: 700 }}
        >
          {consentFormData.iaConsent ? 'Sí, quiero usar IA' : 'No, prefiero no usar IA'}
        </Button>
      </Box>
      {consentFormData.iaConsent && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Genial! Podrás tomar o subir fotos de tus platos y la IA detectará los vegetales automáticamente.
        </Alert>
      )}
      {!consentFormData.iaConsent && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Podrás activar esta función más adelante desde tu perfil si cambias de opinión.
        </Alert>
      )}
    </>
  );

  const renderConfirmForm = () => (
    <>
      <Typography variant="h6" fontWeight={600} mb={3} color="primary">
        Confirmación final
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Revisa tus datos y acepta los Términos y Condiciones para finalizar tu registro.
        </Typography>
      </Alert>
      {/* Resumen de datos */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="primary">Nombre completo:</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>{basicFormData.fullName}</Typography>
        <Typography variant="subtitle2" color="primary">Correo electrónico:</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>{basicFormData.email}</Typography>
        <Typography variant="subtitle2" color="primary">País/región:</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>{configFormData.country}</Typography>
        <Typography variant="subtitle2" color="primary">Objetivo semanal de vegentales:</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>{configFormData.weeklyGoal}</Typography>
        <Typography variant="subtitle2" color="primary">Vegetales a excluir:</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>{configFormData.excludedVegetables.length > 0 ? configFormData.excludedVegetables.join(', ') : 'Ninguna'}</Typography>
        <Typography variant="subtitle2" color="primary">¿Análisis con IA?:</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>{consentFormData.iaConsent ? 'Sí' : 'No'}</Typography>
      </Box>
      {/* Checkbox de términos */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <input
          type="checkbox"
          id="accept-terms"
          checked={confirmFormData.acceptedTerms}
          onChange={e => handleConfirmInputChange(e.target.checked)}
          style={{ marginRight: 12, width: 22, height: 22 }}
        />
        <label htmlFor="accept-terms" style={{ fontSize: 15 }}>
          Acepto los <a href="#" target="_blank" rel="noopener noreferrer">Términos y Condiciones</a> y la <a href="#" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>
        </label>
      </Box>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
      )}
      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>¡Registro completado con éxito! 🎉</Alert>
      )}
    </>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f4faef', 
      p: 2, 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Progreso */}
      <Box sx={{ width: '100%', maxWidth: 400, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" mb={1} align="center">
          {getStepTitle()}
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={getStepProgress()} 
          sx={{ 
            height: 6, 
            borderRadius: 3,
            bgcolor: '#e8f5e9',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#4caf50'
            }
          }} 
        />
      </Box>

      {/* Título principal */}
      <Typography variant="h4" fontWeight={700} color="primary" mb={1} align="center">
        {currentStep === 1 ? '¡Bienvenido a GreenBalance!' : currentStep === 2 ? '¡Personaliza tu experiencia!' : currentStep === 3 ? '¡Consentimiento IA!' : '¡Confirmación final!'}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={4} align="center" sx={{ maxWidth: 350 }}>
        {currentStep === 1 
          ? 'Comencemos creando tu cuenta para un análisis nutricional personalizado'
          : currentStep === 2
            ? 'Configura tus preferencias para obtener recomendaciones más precisas'
            : currentStep === 3
              ? '¡Consentimiento IA!'
              : '¡Confirmación final!'
        }
      </Typography>

      {/* Formulario */}
      <Paper elevation={3} sx={{ 
        p: 4, 
        width: '100%', 
        maxWidth: 400, 
        borderRadius: 4,
        bgcolor: 'white'
      }}>
        {currentStep === 1
          ? renderBasicForm()
          : currentStep === 2
            ? renderConfigForm()
            : currentStep === 3
              ? renderConsentForm()
              : renderConfirmForm()
        }

        {/* Botones de navegación */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          {currentStep > 1 && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handlePrevStep}
              startIcon={<ArrowBack />}
              sx={{ 
                borderRadius: 3, 
                fontWeight: 600,
                textTransform: 'none',
                flex: 1
              }}
            >
              Anterior
            </Button>
          )}
          <Button
            fullWidth={currentStep === 1}
            variant="contained"
            color="primary"
            size="large"
            onClick={handleNextStep}
            sx={{ 
              borderRadius: 3, 
              fontWeight: 700, 
              fontSize: '1.1rem',
              py: 1.5,
              textTransform: 'none',
              flex: currentStep > 1 ? 2 : 1
            }}
            disabled={isSubmitting || (currentStep === 4 && !confirmFormData.acceptedTerms)}
          >
            {isSubmitting && currentStep === 4 ? 'Registrando...' : (currentStep < 3 ? 'Siguiente' : currentStep === 3 ? 'Continuar' : 'Registrarme')}
          </Button>
        </Box>

        {/* Link de inicio de sesión - solo en la primera pantalla */}
        {currentStep === 1 && (
          <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
            ¿Ya tienes cuenta?{' '}
            <Typography 
              component="span" 
              color="primary" 
              sx={{ cursor: 'pointer', fontWeight: 600 }}
              onClick={() => navigate('/login')}
            >
              Inicia sesión aquí
            </Typography>
          </Typography>
        )}
      </Paper>
    </Box>
  );
}